import { useState } from "react";
import {
  HISTORY_LABELS, TIMING_CLASSES, TIMING_LABELS, VERIFICATION_CLASSES, VERIFICATION_LABELS, formatDay, splitMandates, termSummary,
} from "../lib/mandates";
import useMandateTypes from "../hooks/useMandateTypes";
import { MandateEndDialog, MandateFormDialog, MandateReplaceDialog, MandateUnverifyDialog, MandateVerifyDialog } from "./mandates/MandateDialogs";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "./ui/responsive-dropdown-menu";
import { ChevronDown, ChevronRight, Landmark, MoreHorizontal, Plus } from "lucide-react";

const Pill = ({ className, children, testid }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium ${className}`} data-testid={testid}>{children}</span>
);

function HistoryList({ rows }) {
  if (!rows?.length) return <p className="text-xs text-slate-500">Aucun événement.</p>;
  return (
    <ul className="space-y-1" data-testid="mandate-history">
      {rows.map((row) => (
        <li key={row.id} className="text-xs text-slate-600">
          <span className="text-slate-500">{new Date(row.at).toLocaleDateString("fr-FR")}</span> · {HISTORY_LABELS[row.kind] || row.kind}
          {row.reason ? <span className="text-slate-500"> — {row.reason}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function MandateRow({ m, admin, onAction }) {
  const [history, setHistory] = useState(false);
  const verified = m.verification === "verified";
  return (
    <li className="space-y-2 py-3" data-testid={`mandate-${m.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-slate-900" data-testid="mandate-type">{m.type.label}</span>
            <Pill className={TIMING_CLASSES[m.timing]} testid="mandate-timing">{TIMING_LABELS[m.timing]}</Pill>
            <Pill className={VERIFICATION_CLASSES[m.verification]} testid="mandate-verification">{VERIFICATION_LABELS[m.verification]}</Pill>
          </div>
          <div className="text-xs text-slate-600" data-testid="mandate-dates">
            Depuis le {formatDay(m.start_date)}{m.end_date ? ` jusqu'au ${formatDay(m.end_date)}` : ""}
            {m.appointment_date ? ` · acte du ${formatDay(m.appointment_date)}` : ""}
          </div>
          {m.act_reference && <div className="text-xs text-slate-600 break-words">{m.act_reference}</div>}
          <p className={`text-xs ${m.timing === "term_to_review" ? "text-amber-800" : "text-slate-500"}`} data-testid="mandate-term">{termSummary(m)}</p>
          {m.end_reason && <p className="text-xs text-slate-500">Fin : {m.end_reason}</p>}
        </div>
        {admin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Actions sur ce mandat" data-testid={`mandate-menu-${m.id}`}><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" title="Mandat">
              {!verified && <DropdownMenuItem onClick={() => onAction("verify", m)} data-testid="mandate-action-verify">Confirmer la vérification</DropdownMenuItem>}
              {verified && <DropdownMenuItem onClick={() => onAction("unverify", m)} data-testid="mandate-action-unverify">Retirer la vérification</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => onAction("edit", m)} data-testid="mandate-action-edit">Corriger</DropdownMenuItem>
              {m.timing !== "ended" && <DropdownMenuSeparator />}
              {m.timing !== "ended" && !m.replaced_by && <DropdownMenuItem onClick={() => onAction("replace", m)} data-testid="mandate-action-replace">Renouveler ou transmettre</DropdownMenuItem>}
              {m.timing !== "ended" && <DropdownMenuItem onClick={() => onAction("end", m)} data-testid="mandate-action-end">Terminer le mandat</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {m.evidence.length > 0 ? (
        <ul className="space-y-0.5" data-testid="mandate-evidence">
          {m.evidence.map((e) => (
            <li key={`${e.kind}:${e.ref}`} className="text-xs text-slate-600">
              {e.accessible === false ? <span className="text-slate-500">Justificatif non accessible pour vous</span> : <><span className="font-medium">{e.number}</span> · {e.title}</>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-500">Aucun justificatif joint.</p>
      )}
      {verified && m.verification_detail && (
        <p className="text-xs text-emerald-800" data-testid="mandate-verified-by">
          Vérifié par {m.verification_detail.by_name || "un administrateur"} le {new Date(m.verification_detail.at).toLocaleDateString("fr-FR")}
          {m.verification_detail.note ? ` — ${m.verification_detail.note}` : ""}
        </p>
      )}
      <button type="button" onClick={() => setHistory((v) => !v)} className="inline-flex min-h-8 items-center gap-1 text-xs text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora" aria-expanded={history} data-testid="mandate-history-toggle">
        {history ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Historique ({m.history.length})
      </button>
      {history && <HistoryList rows={m.history} />}
    </li>
  );
}

// Mandats légaux d'une personne : ce que la loi lui confie. Ils ne changent ni sa fonction, ni son responsable, ni son rang, ni ses droits dans le CRM.
// L'administrateur seul les écrit ; un lecteur des détails (ou la personne elle-même) les consulte.
export default function CollaboratorMandates({ fiche, mandates, isAdmin, features, onHistoryChange }) {
  const { types, reload: reloadTypes } = useMandateTypes();
  const [dialog, setDialog] = useState(null); // { kind: "new" | "edit" | "end" | "replace" | "verify" | "unverify", mandate? }
  const [showPast, setShowPast] = useState(false);
  const { current, past } = splitMandates(mandates.items);

  const close = () => setDialog(null);
  const done = async () => { await mandates.reload(); await reloadTypes(); onHistoryChange?.(); };
  const open = (kind, mandate) => setDialog({ kind, mandate });
  const writable = isAdmin && !fiche.archived;

  return (
    <Card className="p-5 space-y-3" data-testid="collab-mandates">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display text-sm font-semibold text-slate-900"><Landmark size={15} /> Mandats légaux</div>
        {writable && (
          <Button variant="outline" size="sm" onClick={() => open("new")} data-testid="btn-mandate-new"><Plus size={14} className="mr-1.5" /> Ajouter un mandat</Button>
        )}
      </div>
      <p className="text-xs text-slate-500">Ce que la loi confie à cette personne (gérant…). Cela ne change ni son responsable, ni son rang dans l'organigramme, ni ses droits dans le CRM.</p>

      {mandates.status === "loading" && <Skeleton className="h-16 w-full" />}
      {mandates.status === "error" && (
        <div className="space-y-2 text-sm text-slate-600" data-testid="mandates-error">
          <p>Impossible de charger les mandats.</p>
          <Button variant="outline" size="sm" onClick={mandates.reload}>Réessayer</Button>
        </div>
      )}
      {mandates.status === "ready" && mandates.items.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="mandates-empty">Aucun mandat légal enregistré{writable ? " : ajoutez-en un si cette personne exerce un mandat (gérant, par exemple)." : "."}</p>
      )}
      {current.length > 0 && <ul className="divide-y divide-slate-100" data-testid="mandates-current">{current.map((m) => <MandateRow key={m.id} m={m} admin={writable} onAction={open} />)}</ul>}
      {past.length > 0 && (
        <div className="space-y-1">
          <button type="button" onClick={() => setShowPast((v) => !v)} className="inline-flex min-h-9 items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora" aria-expanded={showPast} data-testid="mandates-past-toggle">
            {showPast ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Mandats terminés ({past.length})
          </button>
          {showPast && <ul className="divide-y divide-slate-100" data-testid="mandates-past">{past.map((m) => <MandateRow key={m.id} m={m} admin={writable} onAction={open} />)}</ul>}
        </div>
      )}

      <MandateFormDialog open={dialog?.kind === "new" || dialog?.kind === "edit"} onOpenChange={(v) => !v && close()} fiche={fiche} mandate={dialog?.kind === "edit" ? dialog.mandate : null}
        types={types} features={features} onDone={done} onConflict={mandates.reload} />
      <MandateEndDialog open={dialog?.kind === "end"} onOpenChange={(v) => !v && close()} mandate={dialog?.mandate} onDone={done} onConflict={mandates.reload} />
      <MandateReplaceDialog open={dialog?.kind === "replace"} onOpenChange={(v) => !v && close()} fiche={fiche} mandate={dialog?.mandate} features={features} onDone={done} onConflict={mandates.reload} />
      <MandateVerifyDialog open={dialog?.kind === "verify"} onOpenChange={(v) => !v && close()} mandate={dialog?.mandate} onDone={done} onConflict={mandates.reload} />
      <MandateUnverifyDialog open={dialog?.kind === "unverify"} onOpenChange={(v) => !v && close()} mandate={dialog?.mandate} onDone={done} onConflict={mandates.reload} />
    </Card>
  );
}
